import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../../utils/roles/client-roles';
import { Messages } from '../../../../utils/message-constants';
import { UserKeys, runBulkOp } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { BulkImportModel } from '../../../../../types';
import { useUserSettings } from '../../../../utils/use-user-settings';

export function useBulkOpActions() {
  const [, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const { changeLogRoles } = useUserSettings();

  const closeDrawer = () => {
    setSearchParams((prev) => {
      prev.delete('selectedBulkOp');
      return prev;
    });
  };

  const hasBulkOpsPermission = (toolsRoles: string[]) => {
    const bulkOpsPermissionValues = Object.values(ToolsRoles)
      .filter((role) => role.isBulkOp)
      .map((role) => role.value);
    return (
      toolsRoles.includes('*') ||
      bulkOpsPermissionValues.some((value) => toolsRoles.includes(value))
    );
  };

  const getPermissionDisabledReason = (
    toolsRoles: string[],
    role: string
  ): string | undefined => {
    if (isAbleToSeeUIElement(toolsRoles, role)) return undefined;

    const matchingRole = Object.keys(ToolsRoles).find(
      (key) => ToolsRoles[key].value === role
    );
    return matchingRole
      ? Messages.Errors.Permission.Acting(ToolsRoles[matchingRole].name)
      : Messages.Errors.Permission.Acting(role);
  };

  const getSpreadsheetUrl = (
    bulkOp: BulkImportModel,
    loggedInUserEmail: string
  ) => {
    const domain = loggedInUserEmail?.includes('@')
      ? loggedInUserEmail.split('@')[1]
      : '';
    return `https://docs.google.com/a/${domain}/spreadsheet/ccc?key=${bulkOp.spreadsheetId}#gid=0`;
  };

  const handleViewSpreadsheet = (
    bulkOp: BulkImportModel,
    loggedInUserEmail: string
  ) => {
    window.open(
      getSpreadsheetUrl(bulkOp, loggedInUserEmail),
      '_blank',
      'noopener, noreferrer'
    );
  };

  const patchLastRan = (bulkOpId: number) => {
    const ranAt = Date.now();
    queryClient.setQueryData<BulkImportModel[]>(
      [UserKeys.BulkOps],
      (oldData) =>
        oldData?.map((bulkOp) =>
          bulkOp.id === bulkOpId
            ? { ...bulkOp, lastRan: ranAt, status: 'RUNNING' }
            : bulkOp
        )
    );
  };

  const patchLastExportUpdate = (bulkOpId: number) => {
    const exportedAt = Date.now();
    queryClient.setQueryData<BulkImportModel[]>(
      [UserKeys.BulkOps],
      (oldData) =>
        oldData?.map((bulkOp) =>
          bulkOp.id === bulkOpId
            ? { ...bulkOp, lastExportUpdate: exportedAt, status: 'RUNNING' }
            : bulkOp
        )
    );
  };

  const { mutate: runDirectBulkOp, isPending: isRunningBulkOp } = useMutation({
    mutationFn: ({
      bulkOp,
      changeLogMessage,
    }: {
      bulkOp: BulkImportModel;
      changeLogMessage: string;
    }) => runBulkOp(bulkOp, null, changeLogMessage),
    onSuccess: (_returnData, variables) => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: Messages.Success.Started('Bulk operation'),
      });
      patchLastRan(variables.bulkOp.id);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Running('this bulk operation'),
      });
    },
    onSettled: (_returnData, _error, variables) => {
      createEvent(analytics.import_export.bulk_operations.run_bulk_operation, {
        module: ModuleList.modules.importExport.name,
        section: ModuleList.modules.importExport.sections.bulkOperations,
        bulkOperationType: variables.bulkOp.bulkImport,
      });
    },
  });

  return {
    closeDrawer,
    hasBulkOpsPermission,
    getPermissionDisabledReason,
    handleViewSpreadsheet,
    patchLastRan,
    patchLastExportUpdate,
    runDirectBulkOp,
    isRunningBulkOp,
    changeLogRoles,
  };
}
